import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    ChevronDown, ChevronRight, RefreshCw, Layers,
    Image as ImageIcon, X
} from 'lucide-react';

interface AdminReferencePlaceTableProps {
    filteredPlaces: any[];
    allPlacesExpanded: boolean;
    onToggleExpand: () => void;
    existingGeoRefNames: Set<string>;
    imagePromptDetails: Map<string, any>;
    getPlaceByName: (name: string) => any;
    generatingImages: Set<string>;
    generatingPrompts: Set<string>;
    regeneratingPlaces: Set<string>;
    getMissingPeriods: (name: string) => { total: number; covered: number; missing: string[] };
    getMissingImages: (name: string) => { total: number; generated: number; missing: string[] };
    onRegeneratePlace: (name: string) => void;
    onGeneratePrompts: (name: string) => void;
    onGenerateImages: (name: string) => void;
    onGenerateMissingImages: (name: string, missing: string[]) => void;
    onViewPrompts: (name: string) => void;
}

export const AdminReferencePlaceTable = React.memo(({
    filteredPlaces,
    allPlacesExpanded,
    onToggleExpand,
    existingGeoRefNames,
    imagePromptDetails,
    getPlaceByName,
    generatingImages,
    generatingPrompts,
    regeneratingPlaces,
    getMissingPeriods,
    getMissingImages,
    onRegeneratePlace,
    onGeneratePrompts,
    onGenerateImages,
    onGenerateMissingImages,
    onViewPrompts
}: AdminReferencePlaceTableProps) => {
    return (
        <div className="border rounded-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 p-3 bg-muted/30">
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={onToggleExpand}
                    className="h-8 w-8 p-0"
                >
                    {allPlacesExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                    ) : (
                        <ChevronRight className="h-4 w-4" />
                    )}
                </Button>
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">All Places</span>
                            <span className="text-xs text-muted-foreground">
                                ({filteredPlaces.length} total)
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded Place List */}
            {allPlacesExpanded && (
                <div className="border-t">
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-muted/50 sticky top-0">
                                <tr className="border-b">
                                    <th className="p-2 text-left font-medium w-48">Place Name</th>
                                    <th className="p-2 text-center font-medium w-32">Data</th>
                                    <th className="p-2 text-center font-medium w-32">Prompts</th>
                                    <th className="p-2 text-center font-medium w-32">Ruins</th>
                                    <th className="p-2 text-center font-medium w-40">Reconstructions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPlaces.map((place: any) => {
                                    const placeName = place.placeName;
                                    const hasPlaceData = existingGeoRefNames.has(placeName);
                                    const promptData = imagePromptDetails.get(placeName);
                                    const hasPrompts = !!promptData;

                                    const placeData = getPlaceByName(placeName);
                                    const ruinsUrl = placeData?.images?.ruinsImageUrl;
                                    const reconstructionsByPeriod = placeData?.images?.reconstructionsByPeriod;
                                    const isGeneratingThisPlace = generatingImages.has(placeName);
                                    const isRegeneratingThisPlace = regeneratingPlaces.has(placeName);

                                    const dataVersion = placeData?.generation?.version || (hasPlaceData ? 1 : undefined);
                                    const promptVersion = promptData?.generation?.version || (hasPrompts ? 1 : undefined);

                                    const missingPeriodsInfo = getMissingPeriods(placeName);
                                    const missingImagesInfo = getMissingImages(placeName);

                                    const periodCount = missingImagesInfo.total > 0
                                        ? missingImagesInfo.total
                                        : (reconstructionsByPeriod ? Object.keys(reconstructionsByPeriod).length : 0);

                                    return (
                                        <tr key={placeName} className="border-t hover:bg-muted/30">
                                            <td className="p-2">
                                                <span className="font-mono">{placeName}</span>
                                            </td>
                                            <td className="p-2 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {dataVersion !== undefined ? (
                                                        <Badge
                                                            variant={dataVersion < 2 ? "secondary" : "default"}
                                                            className={`text-[10px] px-1.5 py-0 ${dataVersion < 2
                                                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100'
                                                                : 'bg-green-600'
                                                                }`}
                                                        >
                                                            v{dataVersion}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}

                                                    {hasPlaceData && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => onRegeneratePlace(placeName)}
                                                            disabled={isRegeneratingThisPlace}
                                                            variant="ghost"
                                                            className="text-xs h-6 px-1.5"
                                                            title="Regenerate place data with latest schema"
                                                        >
                                                            {isRegeneratingThisPlace ? (
                                                                <RefreshCw className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <RefreshCw className="h-3 w-3" />
                                                            )}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-2 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {hasPrompts ? (
                                                        <div className="flex flex-col items-center gap-1">
                                                            <Badge
                                                                variant={promptVersion && promptVersion < 2 ? "secondary" : "default"}
                                                                className={`text-[10px] px-1.5 py-0 cursor-pointer hover:opacity-80 transition-opacity ${promptVersion && promptVersion < 2
                                                                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100'
                                                                    : 'bg-green-600'
                                                                    }`}
                                                                title={`Prompt version ${promptVersion || 1} - Click to view prompts`}
                                                                onClick={() => onViewPrompts(placeName)}
                                                            >
                                                                v{promptVersion || 1}
                                                            </Badge>
                                                        </div>
                                                    ) : (
                                                        <X className="h-4 w-4 text-gray-400 inline" />
                                                    )}

                                                    <Button
                                                        size="sm"
                                                        onClick={() => onGeneratePrompts(placeName)}
                                                        disabled={generatingPrompts.has(placeName)}
                                                        variant="ghost"
                                                        className="text-xs h-6 px-1.5"
                                                        title="Generate prompts for this place"
                                                    >
                                                        {generatingPrompts.has(placeName) ? (
                                                            <RefreshCw className="h-3 w-3 animate-spin" />
                                                        ) : (
                                                            <Layers className="h-3 w-3" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="p-2 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {ruinsUrl ? (
                                                        <a href={ruinsUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
                                                            <ImageIcon className="h-4 w-4 text-blue-600 hover:text-blue-800" />
                                                        </a>
                                                    ) : (
                                                        <X className="h-4 w-4 text-gray-400 inline" />
                                                    )}

                                                    <Button
                                                        size="sm"
                                                        onClick={() => onGenerateImages(placeName)}
                                                        disabled={!hasPrompts || isGeneratingThisPlace}
                                                        variant="ghost"
                                                        className={`text-xs h-6 px-1.5 ${!hasPrompts
                                                            ? ''
                                                            : ruinsUrl
                                                                ? 'text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
                                                                : ''
                                                            }`}
                                                        title={
                                                            !hasPrompts
                                                                ? "Generate prompts first"
                                                                : ruinsUrl
                                                                    ? "Regenerate all images"
                                                                    : "Generate all images"
                                                        }
                                                    >
                                                        {isGeneratingThisPlace ? (
                                                            <RefreshCw className="h-3 w-3 animate-spin" />
                                                        ) : ruinsUrl ? (
                                                            <RefreshCw className="h-3 w-3" />
                                                        ) : (
                                                            <ImageIcon className="h-3 w-3" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="p-2 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {missingImagesInfo.total > 0 ? (
                                                        <div className="flex flex-col items-center gap-1">
                                                            <div className="flex items-center gap-1">
                                                                <ImageIcon className={`h-4 w-4 ${missingImagesInfo.missing.length > 0
                                                                    ? 'text-orange-600 dark:text-orange-400'
                                                                    : 'text-green-600 dark:text-green-400'
                                                                    }`} />
                                                                <span className="text-xs text-muted-foreground">×{periodCount}</span>
                                                            </div>
                                                            <span
                                                                className={`text-[10px] ${missingImagesInfo.missing.length > 0
                                                                    ? 'text-orange-600 dark:text-orange-400'
                                                                    : 'text-green-600 dark:text-green-400'
                                                                    }`}
                                                                title={
                                                                    missingImagesInfo.missing.length > 0
                                                                        ? `Missing images for: ${missingImagesInfo.missing.join(', ')}`
                                                                        : 'All period images generated'
                                                                }
                                                            >
                                                                {missingImagesInfo.generated}/{missingImagesInfo.total}
                                                            </span>
                                                        </div>
                                                    ) : periodCount > 0 ? (
                                                        <div className="flex items-center justify-center gap-1">
                                                            <ImageIcon className="h-4 w-4 text-green-600" />
                                                            <span className="text-xs text-muted-foreground">×{periodCount}</span>
                                                        </div>
                                                    ) : (
                                                        <X className="h-4 w-4 text-gray-400 inline" />
                                                    )}

                                                    {ruinsUrl && missingImagesInfo.missing.length > 0 && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => onGenerateMissingImages(placeName, missingImagesInfo.missing)}
                                                            disabled={isGeneratingThisPlace}
                                                            variant="ghost"
                                                            className="text-xs h-6 px-1.5 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                                                            title={`Generate ${missingImagesInfo.missing.length} missing era images: ${missingImagesInfo.missing.join(', ')}`}
                                                        >
                                                            {isGeneratingThisPlace ? (
                                                                <RefreshCw className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <ImageIcon className="h-3 w-3" />
                                                            )}
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
});

AdminReferencePlaceTable.displayName = 'AdminReferencePlaceTable';
